// ─── Rapprocher une opération bancaire d'une ligne PRÉVUE ─────────
// Calcul PUR (aucun accès Firestore) : l'analyse d'import lui passe les
// occurrences du mois, il renvoie la meilleure cible ou rien.
//
// Pourquoi le libellé compte autant que le montant : hors loyer et abonnements,
// le montant prévu ne tombe presque jamais juste (électricité, courses,
// essence). Un rapprochement au centime ne servirait qu'aux cas déjà faciles.
// « PRLV EDF FACTURE 06/26 » doit trouver « Électricité » même à 12 € près.
//
// Rien n'est jamais appliqué d'office : on SUGGÈRE, l'utilisateur valide ligne
// à ligne (même principe que le reste de l'import).
// Extension OBLIGATOIRE : Metro résout sans, Node non — et ce module est
// désormais chargé par `npm test` via `propositions.js` (piège COWORK.md).
import { motifDeLibelle } from './releveBancaire.js';

// Écart relatif maximal retenu quand les montants ne sont pas égaux.
const ECART_MAX = 0.20;
// En dessous, un mot est trop court pour être discriminant (« de », « sur »…).
const LONGUEUR_MOT = 4;
// Score plancher : en dessous, la proposition ferait plus de mal que de bien.
const SCORE_MIN = 35;
// Barème. Un montant PROCHE ne suffit jamais à lui seul (30 < SCORE_MIN) : sans
// rien dans le libellé, « 900 € » proposerait le loyer à 820 € — une fausse
// piste coûte plus cher qu'une absence de suggestion. Un seul mot vraiment
// commun, lui, suffit (35), même si le montant s'écarte beaucoup : c'est le cas
// de l'électricité ou des courses.
const PTS_EXACT = 100;
const PTS_PROCHE_MAX = 30;
const PTS_MOT = 35;

// Vocabulaire de relevé : présent partout, donc ne distingue rien. Sans ce
// filtre, « PRLV EDF » et « PRLV ASSURANCE » se ressembleraient.
const MOTS_VIDES = new Set([
  'prlv', 'prelevement', 'prelevements', 'virement', 'vir', 'carte', 'paiement',
  'facture', 'sepa', 'achat', 'retrait', 'remise', 'cheque', 'chq', 'mensualite',
  'abonnement', 'echeance', 'ech', 'reference', 'ref', 'client', 'mandat',
  'operation', 'compte', 'banque', 'date', 'valeur', 'montant',
]);

const cents = (n) => Math.round(Math.abs(Number(n) || 0) * 100);
const mots = (s) => motifDeLibelle(s).split(' ')
  .filter(m => m.length >= LONGUEUR_MOT && !MOTS_VIDES.has(m));

// Mots significatifs communs aux deux libellés (comparaison normalisée :
// accents, chiffres et ponctuation déjà retirés par motifDeLibelle).
export function motsCommuns(libelleA, libelleB) {
  const a = new Set(mots(libelleA));
  return mots(libelleB).filter(m => a.has(m));
}

// Score d'une occurrence face à une opération. 0 = à ne pas proposer.
//   op         : { libelle, montant }  — montant SIGNÉ (négatif = dépense)
//   occurrence : sortie de occurrencesDuMois (ligne, prevu, statut…)
export function scoreOccurrence(op, occ) {
  const ligne = occ?.ligne;
  if (!ligne) return 0;

  // ── Filtres durs ────────────────────────────────────────────────
  // Sens : un débit ne se rattache pas à un revenu prévu.
  const sensOp = (Number(op?.montant) || 0) >= 0 ? 'revenu' : 'depense';
  if ((ligne.sens === 'revenu' ? 'revenu' : 'depense') !== sensOp) return 0;
  // Une occurrence DÉJÀ réalisée n'est pas une cible : la rattacher compterait
  // la dépense deux fois. Les mois à venir non plus (rien n'y est encore dû).
  if (occ.statut !== 'manquante' && occ.statut !== 'partielle') return 0;

  // ── Montant ─────────────────────────────────────────────────────
  const prevu = cents(occ.prevu);
  const reel = cents(op?.montant);
  let score = 0;
  if (prevu > 0 && reel === prevu) {
    score = PTS_EXACT;
  } else if (prevu > 0) {
    const ecart = Math.abs(reel - prevu) / prevu;
    // Dégressif jusqu'à 0 à la limite de tolérance, et plafonné SOUS le seuil :
    // le montant seul ne déclenche pas de proposition.
    if (ecart <= ECART_MAX) score = Math.round(PTS_PROCHE_MAX * (1 - ecart / ECART_MAX));
  }

  // ── Libellé ─────────────────────────────────────────────────────
  // C'est lui qui rattrape les montants variables — et il suffit seul.
  score += PTS_MOT * motsCommuns(op?.libelle, ligne.libelle).length;

  return score;
}

// Meilleure occurrence pour une opération, ou null.
// `occurrences` : celles du mois d'imputation de l'opération (le filtrage par
// mois est fait par l'appelant, qui seul sait quels mois il a chargés).
export function meilleureLigne(op, occurrences = []) {
  let best = null;
  for (const occ of occurrences) {
    const score = scoreOccurrence(op, occ);
    if (score < SCORE_MIN) continue;
    // À score égal, on garde la première : l'ordre vient des lignes, trié par
    // libellé — stable d'un import à l'autre.
    if (!best || score > best.score) best = { occ, score };
  }
  if (!best) return null;
  const { occ, score } = best;
  return {
    ligneId: occ.ligneId,
    libelle: occ.ligne.libelle || '',
    budgetId: occ.ligne.budgetId || null,
    categorie: occ.ligne.categorie || 'autre',
    mois: occ.mois,
    prevu: occ.prevu,
    ecart: Math.round((Math.abs(Number(op?.montant) || 0) - occ.prevu) * 100) / 100,
    score,
  };
}
